// Base service class following SOC principle - provides common functionality

export interface ServiceError {
  message: string;
  code?: string;
  details?: Record<string, unknown>;
  originalError?: unknown;
}

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: ServiceError;
}

export abstract class BaseService {
  protected handleError(error: unknown, context?: string): ServiceError {
    const serviceError: ServiceError = {
      message: "An unexpected error occurred",
      originalError: error,
    };

    let messageSetFromResponse = false;

    // Prefer structured axios error data first (even if instance of Error)
    if (error && typeof error === "object") {
      const errorObj = error as Record<string, unknown>;

      if (errorObj.response && typeof errorObj.response === "object") {
        const response = errorObj.response as Record<string, unknown>;

        // Extract message from response data
        if (response.data && typeof response.data === "object") {
          const responseData = response.data as Record<string, unknown>;
          if (typeof responseData.detail === "string") {
            serviceError.message = responseData.detail;
            messageSetFromResponse = true;
          } else if (typeof responseData.message === "string") {
            serviceError.message = responseData.message;
            messageSetFromResponse = true;
          }
          if (typeof (responseData as any).code === "string") {
            serviceError.code = (responseData as any).code as string;
          }
        }

        // Set HTTP status in details
        if (typeof response.status === "number") {
          serviceError.details = {
            ...serviceError.details,
            httpStatus: response.status,
            statusText: response.statusText as string | undefined,
          };
        }
      }

      // Fallback to direct message/code properties
      if (!messageSetFromResponse) {
        if (typeof (errorObj as any).message === "string") {
          serviceError.message = (errorObj as any).message as string;
        }
      }
      if (typeof (errorObj as any).code === "string") {
        serviceError.code = (errorObj as any).code as string;
      }
    }

    // Standard Error fallback (only if no API message was set)
    if (!messageSetFromResponse && error instanceof Error) {
      serviceError.message = error.message;
      serviceError.code = serviceError.code || error.name;
    }

    if (context) {
      serviceError.details = { ...serviceError.details, context };
    }

    return serviceError;
  }

  protected logOperation(operation: string, data?: unknown): void {
    // if (process.env.NODE_ENV === "development") {
    //
    // }
  }

  protected logError(operation: string, error: ServiceError): void {
    console.error(`[${this.constructor.name}] ${operation} failed:`, error);
  }

  protected createSuccessResult<T>(data: T): ServiceResult<T> {
    return {
      success: true,
      data,
    };
  }

  protected createErrorResult<T>(
    error: unknown,
    context?: string
  ): ServiceResult<T> {
    const serviceError = this.handleError(error, context);
    this.logError(context || "Operation", serviceError);
    return {
      success: false,
      error: serviceError,
    };
  }
}
